/** Analyst Shadow 판단 차이 관찰 리포트. */
import { useCallback, useEffect, useState } from 'react';
import {
  AnalystLearningReport,
  fetchAnalystLearningReport,
} from '../services/analystLearningApi';
import './AnalystLearning.css';
const DAY_OPTIONS = [1, 7, 30];

export default function AnalystLearning() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<AnalystLearningReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchAnalystLearningReport(days));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analyst Shadow 관찰 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const copy = async (kind: 'claude' | 'codex') => {
    if (!data) return;
    const text = kind === 'claude' ? claudePrompt(data) : codexChecklist(data);
    await navigator.clipboard?.writeText(text);
    setCopied(kind === 'claude' ? 'Claude 리뷰 프롬프트 복사 완료' : 'Codex 체크리스트 복사 완료');
  };

  if (loading && !data) return <div className="learning-page"><div className="learning-state">Shadow 관찰 리포트 로딩 중...</div></div>;
  if (error && !data) return <div className="learning-page"><div className="learning-state error-state">API 오류: {error}</div></div>;
  if (!data) return null;

  return (
    <div className="learning-page">
      <header className="learning-header">
        <div>
          <h1>Analyst Shadow 관찰</h1>
          <p>Rule과 Analyst Shadow의 판단 차이를 관찰합니다. 모델 학습이나 주문에는 영향이 없습니다.</p>
        </div>
        <select value={days} onChange={(event) => setDays(Number(event.target.value))} disabled={loading}>
          {DAY_OPTIONS.map((value) => <option key={value} value={value}>최근 {value}일</option>)}
        </select>
      </header>

      <section className="learning-banner">
        <strong>판단 차이는 성과가 아닙니다.</strong>
        <span>수익 채점과 자동 학습은 연결되지 않았고, 모든 패턴은 검토 후보입니다.</span>
      </section>
      {error && <div className="learning-state error-state">최신 조회 실패: {error}</div>}

      <section className="learning-grid">
        <Metric label="Rule 결정" value={data.summary.rule_count} />
        <Metric label="Shadow 페어" value={data.summary.paired_count} />
        <Metric label="커버리지" value={`${pct(data.summary.pairing_coverage)}%`} />
        <Metric label="불일치율" value={`${pct(data.summary.disagreement_rate)}%`} />
        <Metric label="오늘 백필" value={`${data.auto_backfill.today_processed_count}/${data.auto_backfill.daily_row_limit}`} />
      </section>

      <section className="learning-section">
        <h2>일치 vs 불일치</h2>
        <div className="learning-bars">
          {Object.entries(data.agreement).map(([key, value]) => (
            <span key={key}><small>{labelAgreement(key)}</small><strong>{value}</strong></span>
          ))}
        </div>
      </section>

      <section className="learning-section">
        <h2>방향성</h2>
        <div className="learning-list">
          {data.direction_split.map((item) => (
            <span key={`${item.rule_action}-${item.analyst_action}`}>
              {item.rule_action} {'->'} {item.analyst_action} · {item.label} <strong>{item.count}</strong>
            </span>
          ))}
          {data.direction_split.length === 0 && <p className="learning-empty">데이터 없음</p>}
        </div>
      </section>

      <section className="learning-section">
        <h2>검토 후보</h2>
        <div className="learning-flags">
          {data.flags.map((flag) => (
            <article key={flag.key} className={`learning-flag ${flag.severity}`}>
              <strong>{flag.name}</strong>
              <span>{flag.transition} · {flag.count}건 · {Math.round(flag.ratio * 100)}%</span>
              <small>{flag.sample_warning}</small>
            </article>
          ))}
        </div>
      </section>

      <section className="learning-section">
        <h2>대표 페어</h2>
        <div className="learning-pairs">
          {data.latest_pairs.map((pair) => (
            <article key={`${pair.rule_id}-${pair.shadow_id}`}>
              <strong>{pair.symbol}</strong>
              <span>Rule {pair.rule_action} {'->'} Shadow {pair.analyst_action}</span>
              <small>wt {pair.weighted_total.toFixed(2)} · {pair.reasons.slice(0, 2).join(', ') || 'reason 없음'}</small>
            </article>
          ))}
          {data.latest_pairs.length === 0 && <p className="learning-empty">데이터 없음</p>}
        </div>
      </section>

      <section className="learning-tools">
        <div>
          <h2>검토용 복사 도구</h2>
          <p>외부 호출 없이 현재 리포트를 텍스트로 조립합니다. 복사 후 각 에이전트 대화창에 붙여넣습니다.</p>
        </div>
        <button onClick={() => copy('claude')}>Claude 리뷰 프롬프트 복사</button>
        <button onClick={() => copy('codex')}>Codex 검증 체크리스트 복사</button>
      </section>
      {copied && <div className="learning-state">{copied}</div>}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return <span className="learning-metric"><small>{label}</small><strong>{value}</strong></span>;
}

function pct(value: number) {
  return Math.round(value * 100);
}

function labelAgreement(key: string) {
  return ({ agree: '같은 결론', disagree: '다른 결론', mixed: '부분 다름', unknown: '불명' } as Record<string, string>)[key] || key;
}

function claudePrompt(data: AnalystLearningReport) {
  const flags = data.flags.map((f) => `- ${f.name}: ${f.transition}, ${f.count}건, ${f.sample_warning}`).join('\n');
  return `ATU2 Analyst Shadow 주간 패턴 리뷰 요청\n기간: 최근 ${data.days}일\npaired n=${data.summary.paired_count}, coverage=${pct(data.summary.pairing_coverage)}%, disagreement=${pct(data.summary.disagreement_rate)}%\n\n자동 플래그:\n${flags}\n\n요청: 표본/노이즈 여부, 추가 관찰 항목, 구현하면 안 되는 이유를 검토해줘. outcome 미연결이므로 수익/정확도 단정 금지.`;
}

function codexChecklist(data: AnalystLearningReport) {
  return `ATU2 Analyst Shadow 관찰 구현 검증 체크리스트\n기간: 최근 ${data.days}일, paired n=${data.summary.paired_count}\n\n- [ ] read-only 유지\n- [ ] 주문/RiskManager/broker 경로 변경 없음\n- [ ] rule timestamp 기준 집계 유지\n- [ ] agreement는 rule/shadow action으로 deterministic 계산\n- [ ] outcome/win rate/P&L 미산출\n- [ ] 자동 학습/튜닝처럼 표현하지 않음\n- [ ] 표본 부족 시 관측만 표시\n- [ ] 관련 테스트와 운영 도메인 검증 수행`;
}
